import { ButtonSpeedDial, FloatingLoader, Skeleton } from "@/components";
import { useDebounce } from "@/hooks/useDebounce";
import useReminderMutation from "@/hooks/useReminderMutation";
import { QUERY_KEYS } from "@/libraries/constant";
import { getReminders } from "@/queries";
import { InboxRemindersSearchParamSchema } from "@/schemas";
import Button from "@mui/material/Button";
import { onlineManager, useInfiniteQuery } from "@tanstack/react-query";
import { Link, createFileRoute } from "@tanstack/react-router";
import { useVirtualizer } from "@tanstack/react-virtual";
import { zodSearchValidator } from "@tanstack/router-zod-adapter";
import compact from "lodash/compact";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { useInView } from "react-intersection-observer";
import EmptyInbox from "../-components/EmptyInbox";
import ReminderCard from "../-components/ReminderCard";

export const Route = createFileRoute("/dashboard/inbox/reminders/")({
	validateSearch: zodSearchValidator(InboxRemindersSearchParamSchema),
	loader: ({ context }) => {
		if (onlineManager.isOnline() && !context.isRestoring) {
			return context.queryClient.ensureInfiniteQueryData(getReminders());
		}
		return context.queryClient.getQueryData([QUERY_KEYS.GET_REMINDERS]);
	},
	component: RouteComponent,
});

function RouteComponent() {
	const navigate = Route.useNavigate();
	const searchQ = Route.useSearch({
		select: (s) => s.q || "",
	});
	const debounceQ = useDebounce(searchQ, 300);
	const [loadMoreRef, isLoadMoreRef] = useInView();
	const scrollRef = useRef<HTMLDivElement>(null);

	const remindersQuery = useInfiniteQuery(getReminders());
	const reminders = useMemo(() => {
		const items = compact(remindersQuery.data || []);
		const undoneItems = items.filter((i) => !i.is_done);
		if (!debounceQ) return undoneItems;
		return undoneItems.filter(
			(u) =>
				u.name.toLowerCase().includes(debounceQ) ||
				u.notes?.toLowerCase()?.includes(debounceQ),
		);
	}, [remindersQuery.data, debounceQ]);

	const { checked } = useReminderMutation();

	const isLoading = remindersQuery.isLoading || remindersQuery.isFetching;
	const isEmpty = useMemo(
		() => !isLoading && !reminders.length && !debounceQ,
		[isLoading, debounceQ, reminders],
	);
	const isEmptySearch = useMemo(
		() => !isLoading && !reminders.length && !!debounceQ,
		[isLoading, debounceQ, reminders],
	);

	const reminderVirtualizer = useVirtualizer({
		count: reminders.length,
		overscan: 5,
		gap: 12,
		paddingStart: 16,
		paddingEnd: reminders.length ? 295 : undefined,
		getScrollElement: () => scrollRef.current,
		measureElement: (el) => el.getBoundingClientRect().height,
		estimateSize: () => 50,
	});
	const reminderVirtualizerItems = reminderVirtualizer.getVirtualItems();

	const onClickAdd = useCallback(
		(e: React.MouseEvent<HTMLButtonElement>) => {
			e.preventDefault();
			return navigate({
				to: "/dashboard/inbox/reminders/create",
			});
		},
		[navigate],
	);
	const onClickRadio = (
		e: React.MouseEvent<HTMLButtonElement>,
		reminderId: number,
		is_done: boolean,
	) => {
		e.stopPropagation();
		e.preventDefault();
		return checked.mutate({
			id: reminderId,
			is_done: !is_done,
		});
	};

	// biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
	useEffect(() => {
		if (isLoadMoreRef && !remindersQuery.isFetchingNextPage) {
			remindersQuery.fetchNextPage();
		}
	}, [isLoadMoreRef, remindersQuery.isFetchingNextPage]);

	return (
		<>
			<div
				ref={scrollRef}
				className="relative h-full w-full overflow-y-auto pb-4"
			>
				<FloatingLoader open={isLoading || remindersQuery.isFetchingNextPage} />
				{!isEmpty || !isEmptySearch ? (
					<div
						className="relative w-full"
						style={{
							height: reminderVirtualizer.getTotalSize(),
						}}
					>
						{reminderVirtualizerItems.map((reminderVirtualizerItem) => {
							const reminder = reminders[reminderVirtualizerItem.index];
							return (
								<Link
									key={reminderVirtualizerItem.key}
									data-index={reminderVirtualizerItem.index}
									ref={reminderVirtualizer.measureElement}
									className="container w-full absolute top-0 left-0"
									style={{
										transform: `translateY(${reminderVirtualizerItem.start}px)`,
									}}
									to="/dashboard/inbox/reminders/$id"
									params={{ id: reminder.id }}
								>
									<ReminderCard
										reminder={reminder}
										onClickRadio={onClickRadio}
									/>
								</Link>
							);
						})}
						{remindersQuery.hasNextPage &&
						!remindersQuery.isFetchingNextPage ? (
							<div className="container">
								<Skeleton ref={loadMoreRef} className="w-full h-28" />
							</div>
						) : null}
					</div>
				) : null}
				{isEmpty ? (
					<EmptyInbox
						variant="reminder"
						title="No to-do items yet"
						addLabel="Add Reminder"
						onClickAdd={onClickAdd}
					/>
				) : null}
				{isEmptySearch ? (
					<EmptyInbox title={`No results found for '${debounceQ}'`} />
				) : null}
			</div>
			<Link to="/dashboard/inbox/reminders/completed" className="sticky">
				<Button
					fullWidth
					size="large"
					className="!relative !font-semibold !py-4"
				>
					Show Completed
				</Button>
			</Link>
			<ButtonSpeedDial bottom={0} mb={0} onClickAdd={onClickAdd} />
		</>
	);
}
